import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, VideoIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { ease } from '../../core/format/motion';
import { mediaOr } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
import { fill } from '../../utils/copy';
import { eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';

/**
 * "Eerst je huid, dan pas producten": de werkwijze als ritueel, op salie.
 * Links blijft het beeld staan (sticky) en wisselt zacht per stap, met ín het beeld een klein
 * label dat zegt waar je bent; rechts scrol je door Kijken, Kiezen, Opbouwen en kleurt het
 * actieve cijfer mee. Op de telefoon onder elkaar, elke stap met zijn eigen beeld.
 *
 * Beelden per stap uit Paginateksten (home.story.steps[].media); leeg = het salonbeeld, het
 * portret met producten, het portret buiten. Shell op salie 5,4:1; blush alleen groot.
 */
export function HomeStory() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const copy = pages.home.story;
  const [active, setActive] = useState(0);
  const fallbacks: Media[] = [mediaOr(copy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling in de salon`
  }), {
    image: founder.withProducts,
    alt: `${founder.name} met de producten die ze kiest`,
    focus: {
      x: 50,
      y: 35
    }
  }, {
    image: founder.portraitPalm,
    alt: founder.name,
    focus: {
      x: 50,
      y: 30
    }
  }];
  const imageFor = (i: number): Media => mediaOr(copy.steps[i]?.media ?? {
    image: '',
    alt: ''
  }, fallbacks[i % fallbacks.length]);
  return <section className="bg-sage text-shell" aria-labelledby="home-story" data-id="element-7490" data-mp="nr9shejkjm95">
      <div className="mx-auto max-w-content px-6 pt-20 sm:pt-24 lg:px-10 lg:pt-32" data-id="element-7491" data-mp="irnf953qq60c">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16" data-id="element-7492" data-mp="itqd2bb4x3fm">
          <div className="lg:col-span-7" data-id="element-7493" data-mp="dzm7nwhip3gn">
            {copy.eyebrow && <p className={`${eyebrowCls} text-shell`} data-id="element-7494" data-mp="923utv0dq43d">{fill(copy.eyebrow)}</p>}
            <h2 id="home-story" className={`mt-5 ${sectionHeading}`} data-id="element-7495" data-mp="8zzt9jpydh69">
              <HeadingText heading={copy.heading} accentBlock accentClassName="text-blush" data-id="element-7496" data-mp="51tqsnw5dnr5" />
            </h2>
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-shell lg:col-span-5 lg:self-end" data-id="element-7497" data-mp="3r7gy3qljntkk">{fill(copy.body)}</p>}
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-12 px-6 pb-20 pt-12 sm:pb-24 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-32 lg:pt-16" data-id="element-7498" data-mp="3m9nriwgbwxjr">
        {copy.steps.length > 0 && <div className="hidden lg:col-span-6 lg:block" data-id="element-7499" data-mp="1kghdmlg9ztcj">
            <div className="sticky top-[104px] h-[calc(100svh-140px)] max-h-[780px] overflow-hidden bg-clay" data-id="element-7500" data-mp="1kewoxcgn74ml">
              {copy.steps.map((s, i) => <motion.div key={`${i}-${s.title}`} className="absolute inset-0" initial={false} animate={{
            opacity: i === active ? 1 : 0,
            scale: reduce || i === active ? 1 : 1.04
          }} transition={{
            duration: reduce ? 0 : 0.3,
            ease
          }} aria-hidden={i !== active} data-id="element-7501" data-mp="1wyjtvo9kckss" data-mpi={i}>
                  <MediaView media={imageFor(i)} sizes="45vw" className="h-full w-full" data-id="element-7502" data-mp="1dzz5w52yxvjd" />
                </motion.div>)}
              {/* Waar je bent: de stapnaam en een streepje per stap, ín het beeld. */}
              <div aria-hidden="true" className="absolute bottom-6 left-6 flex items-center gap-4 bg-shell px-4 py-3 text-ink" data-id="element-7503" data-mp="1nzpzm4jpg6ie">
                <span className="flex gap-1.5" data-id="element-7504" data-mp="2uh3d4maksx1i">
                  {copy.steps.map((s, i) => <span key={`${i}-${s.title}`} className="relative block h-0.5 w-6 overflow-hidden bg-ink/15" data-id="element-7505" data-mp="2zg6iy8ydr7aj" data-mpi={i}>
                      <motion.span className="absolute inset-0 origin-left bg-ink" initial={false} animate={{
                  scaleX: i <= active ? 1 : 0
                }} transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7506" data-mp="76jgltd656ls" />
                    </span>)}
                </span>
                <span className="text-[13px]" data-id="element-7507" data-mp="v2e27fhb3du4">{fill(copy.steps[active]?.title ?? '')}</span>
              </div>
            </div>
          </div>}

        <div className={copy.steps.length > 0 ? 'lg:col-span-6' : 'lg:col-span-12'} data-id="element-7508" data-mp="13x291a2j53l1">
          {copy.steps.length > 0 && <ol data-id="element-7509" data-mp="zvqr7qpwdmne">
              {copy.steps.map((s, i) => <Step key={`${i}-${s.title}`} index={i} isActive={i === active} title={fill(s.title)} body={fill(s.body)} media={imageFor(i)} onActive={setActive} data-id="element-7510" data-mp="1dugop7gmpoca" data-mpi={i} />)}
            </ol>}

          {(copy.cta || copy.secondaryCta) && <div className="flex flex-col gap-3 border-t border-shell/25 pt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-7511" data-mp="1ik0xcw63bhvj">
              {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-shell px-7 text-[15px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-7512" data-mp="1nkf6khjfgxv5">
                  {fill(copy.cta)}
                </button>}
              {copy.secondaryCta && <Link to="/behandelingen" className={`${quietLink} text-shell`} data-id="element-7513" data-mp="qtmmz9xacnom">
                  {fill(copy.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7514" data-mp="uw15fsx1llfd" />
                </Link>}
            </div>}
          {copy.consultLine && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className="mt-6 inline-flex min-h-[44px] items-start gap-3 text-left text-[15px] leading-relaxed text-shell underline-offset-4 hover:underline" data-id="element-7515" data-mp="2sokd9avl2z0p">
              <VideoIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7516" data-mp="3bgd9rf9v32d6" />
              {fill(copy.consultLine)}
            </button>}
        </div>
      </div>
    </section>;
}
type StepProps = {
  index: number;
  isActive: boolean;
  title: string;
  body: string;
  media: Media;
  onActive: (i: number) => void;
};
function Step({
  index,
  isActive,
  title,
  body,
  media,
  onActive
}: StepProps) {
  const ref = useRef<HTMLLIElement>(null);
  // "Actief" zodra de stap de middenband van het scherm kruist.
  const inView = useInView(ref, {
    margin: '-45% 0px -45% 0px'
  });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return <li ref={ref} className="border-t border-shell/25 py-10 lg:flex lg:min-h-[62svh] lg:flex-col lg:justify-center lg:py-16" data-id="element-7517" data-mp="ahzqwzykgf6b">
      <MediaView media={media} sizes="100vw" className="mb-7 aspect-[16/10] w-full lg:hidden" data-id="element-7518" data-mp="k9m3p0qjymb6" />
      {/* De volgorde is de boodschap; de lijst is al genummerd voor schermlezers. Het actieve cijfer kleurt mee (computer). */}
      <div className="flex items-baseline gap-5 lg:block" data-id="element-7519" data-mp="u70ikvwtv31d">
        <span aria-hidden="true" className={`font-display text-[3rem] font-light leading-none transition-colors duration-200 ease-soft lg:text-[6rem] ${isActive ? 'text-blush' : 'text-blush lg:text-shell/30'}`} data-id="element-7520" data-mp="y9ndx6spj63b">
          {index + 1}
        </span>
        <h3 className="font-display text-[2rem] font-light leading-none lg:mt-4 lg:text-[2.75rem]" data-id="element-7521" data-mp="3034k0616vyvy">{title}</h3>
      </div>
      <p className="mt-5 max-w-md text-[17px] leading-relaxed text-shell" data-id="element-7522" data-mp="1as97te4m3zrs">{body}</p>
    </li>;
}